import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  name: string;
  email: string;
  username?: string;
  avatar?: string;
  role: string;
  department?: string;
  phone?: string;
  lastLogin?: string;
  permissions?: string[];
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  register: (userData: Partial<User> & { password: string }) => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  refreshToken: () => Promise<void>;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      token: null,

      // Actions
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Mock user data
          const mockUser: User = {
            id: '1',
            name: 'John Doe',
            email,
            username: 'johndoe',
            role: 'admin',
            department: 'Engineering',
            phone: '+1234567890',
            lastLogin: new Date().toISOString(),
            permissions: ['read', 'write', 'admin']
          };

          const mockToken = 'mock-jwt-token-' + Date.now();

          set({
            user: mockUser,
            isAuthenticated: true,
            token: mockToken,
            isLoading: false,
            error: null
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Login failed',
            isLoading: false
          });
        }
      },

      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          token: null,
          error: null
        });
      },

      register: async (userData) => {
        set({ isLoading: true, error: null });
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          const newUser: User = {
            id: Date.now().toString(),
            name: userData.name || '',
            email: userData.email || '',
            username: userData.username,
            role: userData.role || 'user',
            department: userData.department,
            phone: userData.phone,
            permissions: ['read']
          };

          set({
            user: newUser,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Registration failed',
            isLoading: false
          });
        }
      },

      updateUser: (userData) => {
        const currentUser = get().user;
        if (currentUser) {
          set({
            user: { ...currentUser, ...userData }
          });
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      clearError: () => set({ error: null }),

      refreshToken: async () => {
        try {
          // Simulate token refresh
          await new Promise(resolve => setTimeout(resolve, 500));
          const newToken = 'mock-jwt-token-refreshed-' + Date.now();
          set({ token: newToken });
        } catch (error) {
          set({ error: 'Token refresh failed' });
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        token: state.token
      })
    }
  )
);

